import { useLocation, useNavigate } from 'react-router-dom'
import { PageTitle, TabBody } from '../../components/PageTitle'
import { PolicyListPage } from './PolicyListPage'
import { DictionaryListPage } from '../dictionaries/DictionaryListPage'
import { AssistantPage } from '../assistant/AssistantPage'
import { ViolationMessagesPage } from '../violationMessages/ViolationMessagesPage'
import { withTab } from '../../lib/tabs'
import { activeTabFor, screenFor, tabsFor } from '../../lib/screens'
import { session } from '../../lib/api'

/**
 * What the fleet is told to do, and the two things that feed it.
 *
 * Dictionaries are rule inputs and the assistant drafts policies; neither is a destination an
 * operator sets out for. They were separate entries in an Authoring group, which meant three slots in
 * the navigation for one job, and the cost of that was paid on the phone where the navigation had to
 * fit.
 *
 * Nothing here was rewritten. These are the same three pages, rendered as tab bodies, with their data
 * fetching, their tests and their coverage entries untouched - the re-parenting is what made the
 * whole change affordable.
 */
const SCREEN = screenFor('/policies')

export function PoliciesPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const roles = session.identity()?.roles ?? []
  const tabs = tabsFor(SCREEN, roles)
  const tab = activeTabFor(SCREEN, location.search, roles)

  return (
    <div className="tabbed">
      {/*
        The host owns the page's one h1, above the tab strip. Every body below is a TabBody and
        renders an h2, so "exactly one top-level heading per route" is structural rather than
        something each of the seven re-parented screens has to remember - and layout.spec.ts asserts
        it under strict mode, which is how the three screens that had lost theirs were found.
      */}
      <div className="page-header">
        <div>
          <PageTitle>Policies</PageTitle>
          <p>What the fleet runs, the words it looks for, what it tells people, and the assistant that drafts policies.</p>
        </div>
      </div>

      <div className="tab-strip" role="tablist" aria-label={SCREEN.title}>
        {tabs.map(({ key, label }) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            onClick={() =>
              navigate(`/policies${key === SCREEN.defaultTab ? '' : withTab(location.search, key)}`)
            }
            data-testid={`tab-${key}`}
          >
            {label}
          </button>
        ))}
      </div>

      <TabBody>
        {tab === 'dictionaries' ? (
          <DictionaryListPage />
        ) : tab === 'messages' ? (
          <ViolationMessagesPage />
        ) : tab === 'assistant' ? (
          <AssistantPage />
        ) : (
          <PolicyListPage />
        )}
      </TabBody>
    </div>
  )
}
